(function () {
  'use strict';

  function formatLbs(value) {
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return Math.round(num).toLocaleString('en-US');
  }

  function animateNumber(el, target) {
    const end = Number(target);
    if (Number.isNaN(end)) {
      el.textContent = target;
      return;
    }

    const start = 0;
    const duration = 900;
    const startTime = performance.now();

    function frame(now) {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(start + (end - start) * eased);
      el.textContent = current.toLocaleString('en-US');
      if (progress < 1) requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  }

  function initModals(root) {
    const modal = root.querySelector('[data-coffee-modal]');
    if (!modal) return;

    const panel = modal.querySelector('.coffee-detail__modal-panel');
    const titleEl = modal.querySelector('[data-coffee-modal-title]');
    const valueEl = modal.querySelector('[data-coffee-modal-value]');
    const bodyEl = modal.querySelector('[data-coffee-modal-body]');
    const closeButtons = modal.querySelectorAll('[data-coffee-modal-close]');
    let lastFocused = null;

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    }

    function openModal(tile) {
      lastFocused = document.activeElement;
      if (titleEl) titleEl.textContent = tile.dataset.modalLabel || '';
      if (valueEl) valueEl.textContent = tile.dataset.modalValue || '';
      if (bodyEl) bodyEl.innerHTML = tile.dataset.modalBody || '';
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const closeBtn = modal.querySelector('.coffee-detail__modal-close');
      if (closeBtn) closeBtn.focus();
    }

    root.querySelectorAll('[data-coffee-tile]').forEach(function (tile) {
      tile.addEventListener('click', function () {
        openModal(tile);
      });
    });

    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', function (event) {
      if (event.target.matches('[data-coffee-modal-close], .coffee-detail__modal-backdrop')) {
        closeModal();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!modal.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        closeModal();
        return;
      }

      if (event.key !== 'Tab' || !panel) return;

      const focusable = panel.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function initStoryToggle(root) {
    const story = root.querySelector('[data-coffee-story]');
    const toggle = root.querySelector('[data-coffee-story-toggle]');
    const body = root.querySelector('.coffee-detail__story-body');
    if (!story || !toggle || !body) return;

    function syncToggleVisibility() {
      story.classList.remove('coffee-detail__story--collapsed');
      const overflows = body.scrollHeight > body.clientHeight + 4;
      if (!overflows) {
        toggle.hidden = true;
        return;
      }
      toggle.hidden = false;
      story.classList.add('coffee-detail__story--collapsed');
      toggle.textContent = 'More';
    }

    syncToggleVisibility();
    window.addEventListener('resize', syncToggleVisibility);

    toggle.addEventListener('click', function () {
      const collapsed = story.classList.toggle('coffee-detail__story--collapsed');
      toggle.textContent = collapsed ? 'More' : 'Less';
    });
  }

  function initLiveInventory(root) {
    if (root.dataset.productType === 'blend') return;

    const endpoint = root.dataset.liveEndpoint;
    const productId = root.dataset.productId;
    const inventoryEl = root.querySelector('[data-coffee-inventory-value]');
    if (!endpoint || !productId || !inventoryEl) return;

    const anonKey = root.dataset.supabaseAnonKey;
    const headers = { Accept: 'application/json' };
    if (anonKey) {
      headers.Authorization = 'Bearer ' + anonKey;
      headers.apikey = anonKey;
    }

    function applyInventoryState(data) {
      if (!data || data.inventory_lbs == null) return;

      const lbs = Number(data.inventory_lbs);
      const formatted = formatLbs(data.inventory_lbs) + ' LBS';
      const cell = root.querySelector('[data-coffee-inventory-cell]');

      root.querySelectorAll('[data-coffee-inventory-value]').forEach(function (el) {
        el.dataset.modalValue = formatted;
        const tile = el.closest('[data-coffee-tile]');
        if (tile) tile.dataset.modalValue = formatted;
      });

      if (cell) {
        cell.classList.add('is-live');
        cell.classList.toggle('is-low-stock', !Number.isNaN(lbs) && lbs > 0 && lbs < 50);
        cell.classList.toggle('is-critical-stock', !Number.isNaN(lbs) && lbs > 0 && lbs < 15);
      }

      const roastEl = root.querySelector('[data-coffee-roast-fresh]');
      if (roastEl && data.last_roast_date) {
        const roastDate = new Date(data.last_roast_date);
        if (!Number.isNaN(roastDate.getTime())) {
          const days = Math.floor((Date.now() - roastDate.getTime()) / 86400000);
          if (days >= 0 && days <= 10) {
            roastEl.textContent = days === 0 ? 'Roasted today' : 'Roasted ' + days + 'd ago';
            roastEl.hidden = false;
          } else {
            roastEl.hidden = true;
          }
        }
      }

      const primary = root.querySelector('[data-coffee-inventory-value]');
      if (primary && !primary.dataset.animated) {
        primary.dataset.animated = '1';
        animateNumber(primary, data.inventory_lbs);
        window.setTimeout(function () {
          root.querySelectorAll('[data-coffee-inventory-value]').forEach(function (el) {
            el.textContent = formatted;
          });
        }, 950);
      } else {
        root.querySelectorAll('[data-coffee-inventory-value]').forEach(function (el) {
          el.textContent = formatted;
        });
      }
    }

    function fetchInventory() {
      fetch(endpoint + '?product_id=' + encodeURIComponent(productId), { headers })
        .then(function (res) {
          if (!res.ok) return null;
          return res.json();
        })
        .then(applyInventoryState)
        .catch(function () {
          /* keep synced metafield fallback */
        });
    }

    fetchInventory();
    window.setInterval(fetchInventory, 60000);
  }

  function initRibbonScroll(root) {
    const wrap = root.querySelector('[data-coffee-ribbon-wrap]');
    const ribbon = root.querySelector('[data-coffee-ribbon]');
    if (!wrap || !ribbon) return;

    function sync() {
      const maxScroll = ribbon.scrollWidth - ribbon.clientWidth;
      wrap.classList.toggle('can-scroll-left', ribbon.scrollLeft > 4);
      wrap.classList.toggle('can-scroll-right', maxScroll > 4 && ribbon.scrollLeft < maxScroll - 4);
      wrap.classList.toggle('is-scrolled', ribbon.scrollLeft > 8);
    }

    ribbon.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  function initRevealOnScroll(root) {
    const items = root.querySelectorAll('[data-coffee-reveal]');
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-inview');
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            observer.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  }

  function initBrewToggle(root) {
    const brew = root.querySelector('[data-coffee-brew]');
    if (!brew) return;

    const tabs = brew.querySelectorAll('[data-coffee-brew-tab]');
    const panels = brew.querySelectorAll('[data-coffee-brew-panel]');
    if (!tabs.length) return;

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        const target = tab.dataset.coffeeBrewTab;
        tabs.forEach(function (item) {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach(function (panel) {
          panel.classList.toggle('is-active', panel.dataset.coffeeBrewPanel === target);
        });
      });
    });
  }

  function formatTimer(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins + ':' + String(secs).padStart(2, '0');
  }

  function initBrewTimer(root) {
    root.querySelectorAll('[data-brew-timer]').forEach(function (timer) {
      const totalSeconds = Number(timer.dataset.brewSeconds) || 210;
      const display = timer.querySelector('[data-brew-timer-display]');
      const startBtn = timer.querySelector('[data-brew-timer-start]');
      const resetBtn = timer.querySelector('[data-brew-timer-reset]');
      if (!display || !startBtn || !resetBtn) return;

      let remaining = totalSeconds;
      let intervalId = null;
      let running = false;

      function syncDisplay() {
        display.textContent = formatTimer(remaining);
        timer.classList.toggle('is-running', running);
        timer.classList.toggle('is-done', !running && remaining === 0);
      }

      function stopTimer() {
        running = false;
        if (intervalId) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        startBtn.textContent = remaining > 0 ? 'Start' : 'Done';
        resetBtn.hidden = remaining === totalSeconds;
        syncDisplay();
      }

      function startTimer() {
        if (remaining <= 0) {
          remaining = totalSeconds;
        }
        if (running) {
          stopTimer();
          return;
        }
        running = true;
        startBtn.textContent = 'Pause';
        resetBtn.hidden = false;
        syncDisplay();
        intervalId = window.setInterval(function () {
          remaining -= 1;
          if (remaining <= 0) {
            remaining = 0;
            stopTimer();
            if (window.navigator && window.navigator.vibrate) {
              window.navigator.vibrate([120, 80, 120]);
            }
            return;
          }
          syncDisplay();
        }, 1000);
      }

      function resetTimer() {
        stopTimer();
        remaining = totalSeconds;
        startBtn.textContent = 'Start';
        resetBtn.hidden = true;
        syncDisplay();
      }

      startBtn.addEventListener('click', startTimer);
      resetBtn.addEventListener('click', resetTimer);
      syncDisplay();
    });
  }

  function initStickyBuy(root) {
    const sticky = root.querySelector('[data-coffee-sticky-buy]');
    const hero = root.querySelector('[data-coffee-hero]');
    const mainSubmit = root.querySelector('[data-coffee-submit]');
    const stickySubmit = root.querySelector('[data-coffee-sticky-submit]');
    const stickyPrice = root.querySelector('[data-coffee-sticky-price]');
    const submitPrice = root.querySelector('[data-coffee-submit-price]');
    if (!sticky || !hero || !mainSubmit || !stickySubmit) return;

    function syncStickyState() {
      const heroBottom = hero.getBoundingClientRect().bottom;
      const show = heroBottom < 72 && window.matchMedia('(max-width: 989px)').matches;
      sticky.classList.toggle('is-visible', show);
      sticky.hidden = !show;
      document.body.classList.toggle('coffee-detail-sticky-active', show);
    }

    stickySubmit.addEventListener('click', function () {
      if (!mainSubmit.disabled) mainSubmit.click();
    });

    if (stickyPrice && submitPrice) {
      const observer = new MutationObserver(function () {
        stickyPrice.textContent = submitPrice.textContent;
      });
      observer.observe(submitPrice, { childList: true, characterData: true, subtree: true });
      stickyPrice.textContent = submitPrice.textContent;
    }

    const submitObserver = new MutationObserver(function () {
      stickySubmit.disabled = mainSubmit.disabled;
      stickySubmit.textContent = mainSubmit.disabled ? 'Sold Out' : 'Add to Cart';
    });
    submitObserver.observe(mainSubmit, { attributes: true, attributeFilter: ['disabled'] });
    stickySubmit.disabled = mainSubmit.disabled;

    syncStickyState();
    window.addEventListener('scroll', syncStickyState, { passive: true });
    window.addEventListener('resize', syncStickyState);
  }

  function initFontsAndReveal(root) {
    var revealed = false;

    function ready() {
      if (revealed) return;
      revealed = true;
      root.classList.add('fonts-ready', 'is-ready');
    }

    window.setTimeout(ready, 1400);

    if (document.fonts && document.fonts.load) {
      Promise.all([
        document.fonts.load('400 16px "Pink Sunset"'),
        document.fonts.load('400 48px "Pink Sunset"'),
      ])
        .then(ready)
        .catch(ready);
    } else {
      ready();
    }
  }

  function initHeroScroll(root) {
    const hero = root.querySelector('[data-coffee-hero]');
    const stage = root.querySelector('[data-coffee-stage]');
    if (!hero || !stage) return;

    let ticking = false;
    let scrolledPastHero = false;

    function update() {
      ticking = false;
      const stageTop = stage.getBoundingClientRect().top;

      if (!scrolledPastHero && stageTop <= -32) {
        scrolledPastHero = true;
      } else if (scrolledPastHero && stageTop > 12) {
        scrolledPastHero = false;
      }

      root.classList.toggle('is-past-hero', scrolledPastHero);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  function syncSubmitPrice(root) {
    const priceWrap = root.querySelector('.coffee-detail__buy-price');
    const submitPrice = root.querySelector('[data-coffee-submit-price]');
    if (!priceWrap || !submitPrice) return;

    function readPriceText() {
      const priceItem = priceWrap.querySelector('.price-item--regular, .price-item--sale, .price-item');
      return priceItem ? priceItem.textContent.trim() : '';
    }

    function apply() {
      const text = readPriceText();
      if (text) submitPrice.textContent = text;
    }

    apply();

    const observer = new MutationObserver(apply);
    observer.observe(priceWrap, { childList: true, subtree: true, characterData: true });

    root.querySelectorAll('.coffee-detail__buy input[type="radio"]').forEach(function (input) {
      input.addEventListener('change', function () {
        window.setTimeout(apply, 0);
      });
    });
  }

  // The Onyx PDP renders its own bag visual and has no Dawn <media-gallery>.
  // The shared <variant-radios>/<variant-selects> element (theme global.js)
  // calls updateMedia() on every variant change, which runs
  // `document.getElementById('MediaGallery-' + section).setActiveMedia(...)`.
  // On this section that element doesn't exist, so updateMedia() throws a
  // TypeError BEFORE updateVariantInput() runs in the same handler. The form's
  // hidden [name="id"] input is therefore never moved off the default variant,
  // so choosing "5lb" (or any non-default option) silently adds the default
  // 12oz to the cart. Neutralizing updateMedia() on this section's variant
  // element lets the rest of the change handler (updateVariantInput +
  // renderProductInfo: price + add-button state) run normally.
  function fixVariantPicker(section) {
    section.querySelectorAll('variant-radios, variant-selects').forEach(function (el) {
      el.updateMedia = function () {};
    });
  }

  function roastFetchHeaders(section) {
    const headers = { Accept: 'application/json' };
    const anonKey =
      section.dataset.supabaseAnonKey ||
      (document.querySelector('meta[name="supabase-anon-key"]') || {}).content ||
      '';
    if (anonKey) {
      headers.Authorization = 'Bearer ' + anonKey;
      headers.apikey = anonKey;
    }
    return headers;
  }

  function renderRoastCurveHost(host, data) {
    if (!host || !data || typeof window.renderRoastCurveChart !== 'function') return false;
    if (!Array.isArray(data.bean_temp) || data.bean_temp.length < 2) return false;
    window.renderRoastCurveChart(host, data);
    return true;
  }

  function initRoastCurve(section) {
    const host = section.querySelector('[data-roast-curve]');
    const jsonEl = section.querySelector('[data-roast-curve-json]');
    if (!host || !jsonEl) return;

    let data = null;
    try {
      data = JSON.parse(jsonEl.textContent || '{}');
    } catch (_err) {
      return;
    }

    renderRoastCurveHost(host, data);
  }

  function setRoastCaption(section, text) {
    const caption = section.querySelector('[data-roast-profile-caption]');
    if (caption && text) caption.textContent = text;
  }

  function initRoastProfileLive(section) {
    const endpoint = section.dataset.roastViewEndpoint;
    const productId = section.dataset.productId;
    const host = section.querySelector('[data-roast-curve]');
    if (!endpoint || !productId || !host) return;

    const url =
      endpoint.replace(/\/$/, '') +
      '?view=profile&product_id=' +
      encodeURIComponent(productId);

    fetch(url, { headers: roastFetchHeaders(section) })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (payload) {
        if (!payload || !payload.curve || payload.curve.source !== 'gallatin-diedrich') return;
        if (!renderRoastCurveHost(host, payload.curve)) return;
        setRoastCaption(section, 'Latest roast profile from our Gallatin roastery.');
      })
      .catch(function () {});
  }

  function initBagRoastLookup(section) {
    const endpoint = section.dataset.roastViewEndpoint;
    const productId = section.dataset.productId;
    const block = section.querySelector('[data-roast-bag-lookup]');
    const dateInput = section.querySelector('[data-roast-bag-date]');
    const submitBtn = section.querySelector('[data-roast-bag-submit]');
    const messageEl = section.querySelector('[data-roast-bag-message]');
    const host = section.querySelector('[data-roast-curve]');
    if (!endpoint || !productId || !block || !dateInput || !submitBtn || !host) return;

    function showMessage(text) {
      if (!messageEl) return;
      if (!text) {
        messageEl.hidden = true;
        messageEl.textContent = '';
        return;
      }
      messageEl.hidden = false;
      messageEl.textContent = text;
    }

    submitBtn.addEventListener('click', function () {
      const roastDate = dateInput.value;
      if (!roastDate) {
        showMessage('Choose the roast date from your bag.');
        return;
      }

      showMessage('Loading…');
      const url =
        endpoint.replace(/\/$/, '') +
        '?view=bag&product_id=' +
        encodeURIComponent(productId) +
        '&roast_date=' +
        encodeURIComponent(roastDate);

      fetch(url, { headers: roastFetchHeaders(section) })
        .then(function (res) {
          if (!res.ok) throw new Error('lookup_failed');
          return res.json();
        })
        .then(function (payload) {
          const roasts = payload && payload.roasts ? payload.roasts : [];
          if (!roasts.length) {
            showMessage('No Gallatin roast curve matched that date for this coffee.');
            return;
          }

          const picked = roasts.find(function (row) {
            return row.traced;
          }) || roasts[0];

          if (!renderRoastCurveHost(host, picked.curve)) {
            showMessage('We found a roast that day but could not draw the curve.');
            return;
          }

          var caption = 'Roast curve for the date on your bag (' + roastDate + ').';
          if (picked.traced) caption += ' Traceability confirmed from packaging.';
          else if (roasts.length > 1) caption += ' Multiple roasts matched; showing the closest recorded profile.';
          setRoastCaption(section, caption);
          showMessage('');
        })
        .catch(function () {
          showMessage('Could not load that roast date. Try again in a moment.');
        });
    });
  }

  function initSection(section) {
    fixVariantPicker(section);
    initFontsAndReveal(section);
    initModals(section);
    initStoryToggle(section);
    initLiveInventory(section);
    initHeroScroll(section);
    initRibbonScroll(section);
    initRevealOnScroll(section);
    initBrewToggle(section);
    initBrewTimer(section);
    initStickyBuy(section);
    initRoastCurve(section);
    initRoastProfileLive(section);
    initBagRoastLookup(section);
    syncSubmitPrice(section);
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.coffee-detail').forEach(initSection);
  });
})();
